import {
  LocalStorageKeysEnum,
  MandateTransactionStatusEnum,
  MasterMandateStatusEnum,
  PaymentAppEnum,
  SessionStorageKeysEnum,
  TRANSACTION_STATUS_API_CALL_TIMEOUT,
} from "@/constants/constants";
import {
  CheckoutDetails,
  JuspayMandate,
  MandateDetails,
  MandateDetailsV2,
  PaytmMandate,
  PhonepeMandate,
  RecurringSubscriptionStatus,
} from "@/types/payment.types";
import { captureError, getPlanId, isTvEnv } from "@/utils/common.helpers";
import { API_ROUTES } from "../apiRoutes";
import { REQUEST_METHODS } from "../configs.apiClient";
import { HttpClient } from "../HttpClient";

export class PaymentAPIClient {
  private httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  async createPhonepeMandates({
    targetApp,
    eventProperties,
    couponCode,
    overridingPlanId,
  }: {
    targetApp: string;
    eventProperties: Record<string, string>;
    couponCode?: string;
    overridingPlanId?: string;
  }) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.payment.getPhonepeMandate;

      const queryParams: {
        platform?: string;
        appId?: string;
        os?: string;
      } = {
        // isRecurringSubscriptionBuild: true,
        // isTrialSubscriptionBuildUser: true,
        appId: "in.stage",
        os: "android", // TODO: remove this hardocding after backend is updated
      };

      // TODO: remove this hardocding
      if (isTvEnv()) {
        queryParams.platform = "tv";
      }

      const contentIds = localStorage.getItem(
        LocalStorageKeysEnum.SOURCE_CONTENT_SLUG_BEFORE_PAYING,
      );

      const initialLandingUrl = sessionStorage.getItem(
        SessionStorageKeysEnum.INITIAL_LANDING_URL,
      );

      const body = {
        planId: overridingPlanId ?? (await getPlanId()),
        partnerCoupon: couponCode ?? "",
        targetApp,
        ...eventProperties,
        eventProperties: {
          content_ids: contentIds,
          ...(initialLandingUrl && {
            mandate_creation_deeplink: initialLandingUrl,
          }),
        },
      };

      const response = await this.httpClient.request<PhonepeMandate>({
        endpoint,
        options: {
          method,
        },
        body,
        queryParams,
      });

      return response.data;
    } catch (error) {
      console.error("Error in PaymentAPIClient createPhonepeMandate", error);
      throw captureError(error);
    }
  }

  async createMandate({
    eventProperties,
    paymentApp,
    overridingPlanId,
    paymentGateway,
  }: {
    eventProperties: Record<string, string>;
    paymentApp: PaymentAppEnum;
    overridingPlanId?: string;
    paymentGateway: string;
  }) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.payment.getMandate;

      const queryParams = {
        // isRecurringSubscriptionBuild: true,
        // isTrialSubscriptionBuildUser: true,
        appId: "in.stage",
      };

      const body = {
        ...eventProperties,
        selectedPlan: overridingPlanId ?? (await getPlanId()),
        paymentGateway,
        paymentApp,
      };

      const response = await this.httpClient.nestRequest<{
        responseMessage: string;
        upiIntentLink: string;
      }>({
        endpoint,
        options: {
          method,
        },
        addToken: true,
        body,
        queryParams,
      });

      return response.upiIntentLink;
    } catch (error) {
      console.error("Error in PaymentAPIClient createMandate", error);
      throw captureError(error);
    }
  }

  async createJuspayMandate({
    targetApp,
    eventProperties,
    couponCode,
    overridingPlanId,
  }: {
    targetApp: string;
    eventProperties: Record<string, string>;
    couponCode?: string;
    overridingPlanId?: string;
  }) {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.payment.getJuspayMandate;

      const queryParams = {
        isRecurringSubscriptionBuild: true,
      };

      const body = {
        os: "android",
        planId: overridingPlanId ?? (await getPlanId()),
        coupon: couponCode ?? "",
        upiDeepLinkFlowRequired: true,
        upiPackageName: targetApp,
        ...eventProperties,
      };

      const response = await this.httpClient.request<JuspayMandate>({
        endpoint,
        options: {
          method,
        },
        body,
        queryParams,
      });

      return response.response;
    } catch (error) {
      console.error("Error in PaymentAPIClient createJuspayMandate", error);
      throw captureError(error);
    }
  }

  async createPaytmMandate() {
    try {
      const method = REQUEST_METHODS.POST;
      const endpoint = API_ROUTES.payment.getPaytmMandate;

      const queryParams = {
        appId: "in.stage",

        // isRecurringSubscriptionBuild: true,
      };

      const body = {
        planId: getPlanId(),
        targetApp: "com.paytm.com",
        os: "android",
        appId: "in.stage",
      };

      const response = await this.httpClient.request<PaytmMandate>({
        endpoint,
        options: {
          method,
        },
        body,
        queryParams,
      });

      return response.data;
    } catch (error) {
      console.error("Error in PaymentAPIClient createJuspayMandate", error);
      throw captureError(error);
    }
  }

  async fetchCheckoutDetails(params?: { overridingPlanId?: string }) {
    try {
      const { overridingPlanId } = params ?? {};
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.payment.getCheckoutDetails;

      const queryParams: Record<string, string | boolean | number> = {
        appId: "in.stage",
        planId: overridingPlanId ?? (await getPlanId()),
      };

      const response = await this.httpClient.request<CheckoutDetails>({
        endpoint,
        options: {
          method,
        },
        queryParams,
      });

      return response.data;
    } catch (error) {
      console.error("Error in PaymentAPIClient checkout details", error);
      captureError(error);
      return {
        error: true,
      };
    }
  }

  async getTransactionStatus() {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.payment.transactionStatus;

      const response = await this.httpClient.nestRequest<{
        responseMessage: string;
        txnStatus: MandateTransactionStatusEnum;
      }>({
        endpoint,
        options: {
          method,
          signal: AbortSignal.timeout(TRANSACTION_STATUS_API_CALL_TIMEOUT), // Abort API call after timeout duration to prevent hanging requests
        },
        addToken: true,
      });

      return response.txnStatus;
    } catch (error) {
      console.error("Error in PaymentAPIClient getTransactionStatus", error);
      throw captureError(error);
    }
  }

  async pauseMandate() {
    try {
      const method = REQUEST_METHODS.PATCH;
      const endpoint = API_ROUTES.payment.pauseMandate;

      const response = await this.httpClient.nestRequest<{
        status: MasterMandateStatusEnum;
      }>({
        endpoint,
        options: {
          method,
        },
        addToken: true,
        body: {},
      });

      return response.status;
    } catch (error) {
      console.error("Error in PaymentAPIClient pauseMandate", error);
      throw captureError(error);
    }
  }

  async mandateStatus() {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.payment.mandateStatus;

      const response = await this.httpClient.nestRequest<{
        status: MasterMandateStatusEnum;
      }>({
        endpoint,
        options: {
          method,
        },
        addToken: true,
      });

      return response.status;
    } catch (error) {
      console.error("Error in PaymentAPIClient mandateStatus", error);
      captureError(error);
      return null;
    }
  }

  async getMandateDetails(): Promise<MandateDetails | null> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.payment.mandateDetails;

      const response = await this.httpClient.nestRequest<MandateDetails>({
        endpoint,
        options: {
          method,
        },
      });

      return response;
    } catch (error) {
      console.error("Error in PaymentAPIClient get mandate details", error);
      captureError(error);
      return null;
    }
  }
  async getMandateDetailsV2(): Promise<MandateDetailsV2 | null> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.payment.getMandateDetails;
      const response = await this.httpClient.nestRequest<MandateDetailsV2>({
        endpoint,
        options: {
          method,
        },
      });
      return response;
    } catch (error) {
      console.error("Error in PaymentAPIClient getMandateDetailsV2", error);
      captureError(error);
      return null;
    }
  }
  async getRecurringSubscriptionStatus(currentPgName: string, token?: string) {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.payment.getRecurringSubscriptionStatus;

      const queryParams = {
        currentPgName,
      };

      const response =
        await this.httpClient.request<RecurringSubscriptionStatus>({
          endpoint,
          options: {
            method,
          },
          queryParams,
          token,
        });

      return response;
    } catch (error) {
      console.error(
        "Error in PaymentAPIClient getRecurringSubscriptionStatus",
        error,
      );
      throw captureError(error);
    }
  }
}
